import type {
  ListeningAssessmentContent,
  ListeningAssessmentPartContent,
  ListeningAssessmentQuestion,
  ListeningQuestionOption,
} from "./steps/types";
import {
  asNumber,
  asString,
  normalizeSubmissionProgress,
  type UnknownRecord,
} from "../assessmentNormalization";

const toOptionObject = (
  value: unknown,
  index: number,
): ListeningQuestionOption | null => {
  if (typeof value === "string") {
    return {
      id: String(index + 1),
      label: value,
    };
  }

  if (!value || typeof value !== "object") {
    return null;
  }

  const option = value as UnknownRecord;
  const id = asString(option.id) || String(index + 1);
  const studentImage = asString(option.studentImage || option.student_image);
  const audio = asString(option.audio);
  const label = asString(option.label || option.name || option.student_name);

  return {
    id,
    ...(studentImage ? { studentImage } : {}),
    ...(audio ? { audio } : {}),
    ...(label ? { label } : {}),
  };
};

const normalizeQuestion = (
  raw: unknown,
  index: number,
): ListeningAssessmentQuestion | null => {
  if (!raw || typeof raw !== "object") {
    return null;
  }

  const source = raw as UnknownRecord;
  const questionId = asString(source.question_id || source.questionId);
  const questionType = asString(source.question_type || source.questionType);

  if (!questionId || !questionType) {
    return null;
  }

  const rawOptions = Array.isArray(source.options) ? source.options : [];
  const options: Array<string | ListeningQuestionOption> = rawOptions
    .map((option, optionIndex) => {
      if (typeof option === "string") {
        return option;
      }
      return toOptionObject(option, optionIndex);
    })
    .filter((option): option is string | ListeningQuestionOption =>
      Boolean(option),
    );

  const optionA =
    asString(source.optionA || source.A) ||
    (typeof options[0] === "string" ? options[0] : "");
  const optionB =
    asString(source.optionB || source.B) ||
    (typeof options[1] === "string" ? options[1] : "");

  return {
    question_id: questionId,
    question_type: questionType,
    part_order: asNumber(source.part_order || source.partOrder),
    question_order:
      asNumber(source.question_order || source.questionOrder) || index + 1,
    prompt: asString(source.prompt),
    image: asString(
      source.instruction_image || source.instructionImage || source.image,
    ),
    audio: asString(source.audio_media || source.audioMedia || source.audio),
    promptAudio: asString(source.prompt_audio || source.promptAudio),
    optionA,
    optionB,
    options,
  };
};

const normalizePart = (
  raw: unknown,
): ListeningAssessmentPartContent | undefined => {
  if (!raw || typeof raw !== "object") {
    return undefined;
  }

  const source = raw as UnknownRecord;
  const rawQuestions = Array.isArray(source.questions) ? source.questions : [];
  const rawYesNoQuestions = Array.isArray(source.yesNoQuestions)
    ? source.yesNoQuestions
    : Array.isArray(source.yes_no_questions)
      ? source.yes_no_questions
      : [];
  const rawStudentSelectQuestions = Array.isArray(source.studentSelectQuestions)
    ? source.studentSelectQuestions
    : Array.isArray(source.student_select_questions)
      ? source.student_select_questions
      : [];

  const mergedQuestions = [
    ...rawQuestions,
    ...rawYesNoQuestions,
    ...rawStudentSelectQuestions,
  ];
  const questions = mergedQuestions
    .map((question, index) => normalizeQuestion(question, index))
    .filter((question): question is ListeningAssessmentQuestion =>
      Boolean(question),
    )
    .sort((a, b) => a.question_order - b.question_order);

  return {
    testType: asString(source.testType || source.test_type),
    submissionId: asString(source.submissionId || source.submission_id),
    partOrder: asNumber(source.partOrder || source.part_order),
    prompt: asString(source.prompt || source.text_passage),
    promptImage: asString(source.promptImage || source.prompt_image),
    promptAudio: asString(source.promptAudio || source.prompt_audio),
    questions,
  };
};

const normalizeCharacter = (raw: unknown) => {
  if (!raw || typeof raw !== "object") {
    return undefined;
  }

  const source = raw as UnknownRecord;
  return {
    id: asString(source.id),
    name: asString(source.name),
    image: asString(source.image),
  };
};

const normalizeCharacterGroup = (raw: unknown) => {
  if (!raw || typeof raw !== "object") {
    return undefined;
  }

  const source = raw as UnknownRecord;
  const teacher = normalizeCharacter(source.teacher);
  const students = Array.isArray(source.students)
    ? source.students
        .map(normalizeCharacter)
        .filter((student): student is NonNullable<typeof student> =>
          Boolean(student),
        )
    : undefined;

  return {
    ...(teacher ? { teacher } : {}),
    ...(students ? { students } : {}),
  };
};

export const normalizeListeningAssessmentContent = (
  raw: unknown,
): ListeningAssessmentContent => {
  const source = raw as UnknownRecord;

  return {
    testType: asString(source.testType || source.test_type),
    overallInstructions: asString(
      source.overallInstructions || source.instructions,
    ),
    instructionsAudio: asString(
      source.instructionsAudio || source.instructions_audio,
    ),
    instructionImage: asString(
      source.instructionImage || source.instruction_image,
    ),
    yesNoInstructions: asString(
      source.yesNoInstructions ||
        source.yes_no_instructions ||
        source.mazeInstructions,
    ),
    yesNoInstructionsAudio: asString(
      source.yesNoInstructionsAudio || source.yes_no_instructions_audio,
    ),
    yesNoQuestionAudio: asString(
      source.yesNoQuestionAudio || source.yes_no_question_audio,
    ),
    yesNoInstructionImage: asString(
      source.yesNoInstructionImage || source.yes_no_instruction_image,
    ),
    studentSelectionInstructions: asString(
      source.studentSelectionInstructions ||
        source.student_selection_instructions,
    ),
    studentSelectionInstructionsAudio: asString(
      source.studentSelectionInstructionsAudio ||
        source.student_selection_instructions_audio,
    ),
    studentSelectionQuestionAudio: asString(
      source.studentSelectionQuestionAudio ||
        source.student_selection_question_audio,
    ),
    studentSelectionInstructionImage: asString(
      source.studentSelectionInstructionImage ||
        source.student_selection_instruction_image,
    ),
    recapInstructions: asString(
      source.recapInstructions || source.recap_instructions,
    ),
    recapInstructionsAudio: asString(
      source.recapInstructionsAudio || source.recap_instructions_audio,
    ),
    recapImage: asString(source.recapImage || source.recap_image),
    characterGroup: normalizeCharacterGroup(
      source.characterGroup || source.character_group,
    ),
    part: normalizePart(source.part),
    submissionProgress: normalizeSubmissionProgress(source.submissionProgress),
  };
};

export const normalizeListeningQuestionType = (questionType: string): string =>
  questionType.toLowerCase().replaceAll("-", "_").trim();
